// REACT IMPORTS
import React, { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";

// COMPONENT IMPORTS
import Sidebar from "./sidebar";
import WarningBar from "components/warning/WarningBar";
import Footer from "./footer";
import {
  onCloseWarn,
  useIsLogin,
  useIsSmallSidebar,
  useShowWarn,
} from "store/slices/commonSlice";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  clearSlice,
  downloadPdfFromApi,
  encode,
  replaceMarkers,
} from "utils/HelperFunctions";
import {
  accountPermissionGet,
  checkTokenExpire,
  notificationCountGet,
  updateOverlayState,
  useUserData,
} from "store/slices/authSlice";
import {
  paymentLogoGet,
  paymentMarkersGet,
  paymentReceiptDownloadPdf,
  useHistoryCount,
  useScheduleHistoryCount,
} from "store/slices/paymentsSlice";
import Login from "pages/auth/Login";
import Navbar from "./navbar";
import Drawer from "components/ui-component/SideDrawer";
import NavMenu from "./navbar/NavMenu";
import Helment from "./helmet";
import { INVALID_PATH, invalidRouteState } from "utils/invalidRoute";
import { TOKEN_STORAGE_KEY, TOKEN_STORAGE_TIME } from "store/api/axiosApi";
import {
  useLeadData,
  usePortalSettingsData,
  usePortalVanityUrl,
} from "store/slices/dashboardSlice";
import { decode } from "utils/HelperFunctions";
import { useMediaQuery } from "react-responsive";
import ActionPreview from "components/actionCenter/ActionPreview";
import {
  setPreviewId,
  setPreviewType,
  showPreview,
  useActionCount,
} from "store/slices/actionSlice";
import QuotesPreview from "components/actionCenter/QuotesPreview";
import InvoicePreview from "components/actionCenter/InvoicePreview";
import SubscriptionsPreview from "components/subscriptions/SubscriptionsPreview";
import PaymentPreview from "components/payments/PaymentPreview";
import { RootState } from "store/store";
import { useSubscriptionCount } from "store/slices/subscriptionSlice";
import { useAttachmentList } from "store/slices/documentsSlice";
import {
  countryListGet,
  useCountryList,
} from "store/slices/paymentMethodSlice";
import { CountryOption } from "types/common";
import { SettingsValue } from "types/payments";
import { TestIdScope, useTid } from "utils/testid";
import AiPanelHost from "components/ai/AiPanelHost";
import { isLegacyClusterPath } from "utils/legacyUrlDecoder";

const TOKEN_CHECK_INITIAL_DELAY_MS = 165000;
const TOKEN_CHECK_INTERVAL_MS = 180000;

/* ------------------------------------- LAYOUT PAGE ------------------------------------- */
const Layout: React.FC = () => {
  const tid = useTid();
  const isLessThan306 = useMediaQuery({ query: "(max-width: 306px)" });
  const isWarnEnabled = useShowWarn();
  const isSmallSidebar = useIsSmallSidebar();
  const userData = useUserData();
  const accountLeadData = useLeadData();
  const dispatch = useAppDispatch();
  const isLogin = useIsLogin();
  const location = useLocation();
  const historyCount = useHistoryCount();
  const scheduleHistoryCount = useScheduleHistoryCount();
  const subscriptionCount = useSubscriptionCount();
  const actionCount = useActionCount();
  const attachmentList = useAttachmentList();
  const portalSettings = usePortalSettingsData();
  const vanityUrl = usePortalVanityUrl();
  const navigate = useNavigate();
  const { clusterId: cluster_id, id } = useParams();
  const clusterId = decode(cluster_id || "", true);
  const [isDownloading, setIsDownloading] = useState<string | number>("");
  const [isApiCall, setIsApiCall] = useState<boolean>(false);
  const [isPaymentPage, setIsPaymentPage] = useState<boolean>(false);
  const [openMenu, setOpenMenu] = useState<boolean>(false);
  const countryList: CountryOption[] = useCountryList();
  const { paymentLogo: logo1, paymentMarkers } = useAppSelector(
    (state: RootState) => state.payment
  );
  const { isDashPreview } = useAppSelector(
    (state: RootState) => state.dashboard
  );
  const { isExpired, isExpiry } = useAppSelector(
    (state: RootState) => state.auth
  );
  const { lastTab } = useAppSelector((state: RootState) => state.common);
  const permissionClusterId =
    clusterId || `${userData?.data?.accountData?.clusterid || ""}`;

  useEffect(() => {
    if (!isLogin || !permissionClusterId) {
      return;
    }

    dispatch(accountPermissionGet({ cluster_id: permissionClusterId }));
  }, [dispatch, isLogin, permissionClusterId]);

  // The warning bar is only relevant when the min marinda setting is custom or default.
  const showWarn = useMemo(
    () =>
      isWarnEnabled &&
      (portalSettings?.min_marinda_flag === SettingsValue.CUSTOM ||
        portalSettings?.min_marinda_flag === SettingsValue.DEFAULT),
    [isWarnEnabled, portalSettings?.min_marinda_flag]
  );

  const onGetLogo = async () => {
    try {
      let payload = {
        cluster_id: clusterId,
      };
      await dispatch(paymentLogoGet(payload)).unwrap();
      setIsApiCall(true);
    } catch (error: any) {
      console.log("get logo err: -->", error?.message);
    }
  };
  const { previewId, previewType } = useAppSelector(
    (state: RootState) => state.action
  );
  const { showOverlay } = useAppSelector((state: RootState) => state.auth);
  const isLessThan590 = useMediaQuery({ query: "(max-width:590px)" });

  useEffect(() => {
    if (!logo1 && clusterId && !isApiCall && isLogin) onGetLogo();
  }, [logo1, clusterId]);

  const onCountryListGet = async (): Promise<void> => {
    try {
      await dispatch(countryListGet()).unwrap();
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.log("conntry list get err: -->", error?.message);
    }
  };
  useEffect(() => {
    if (!countryList?.length) {
      onCountryListGet();
    }
  }, [location?.pathname]);

  useEffect(() => {
    // Cluster-scoped routes are normalized by CheckClusterId. Do not race that
    // redirect by navigating with the old cluster slug.
    if (isLegacyClusterPath(cluster_id)) {
      return;
    }

    let arr: string[] | [] = location?.pathname?.split("/");
    if (
      arr[2] !== "make-payment" &&
      arr[2] !== "quick-checkout" &&
      arr[1] !== "view-payment" &&
      arr[1] !== "agreement" &&
      arr[1] !== "payment_link" &&
      arr[1] !== "product" &&
      arr[1] !== "invoice" &&
      arr[1] !== "quotes" &&
      arr[1] !== "subscribe" &&
      arr[1] !== "invalid" &&
      arr[1] !== "subscriptions-plans" &&
      arr[1] !== "payments" &&
      arr[1] !== "payment-methods"
    ) {
      if (!!!localStorage?.getItem(TOKEN_STORAGE_KEY)) {
        clearSlice(dispatch);
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        localStorage.removeItem(TOKEN_STORAGE_TIME);
        let clusterId = cluster_id
          ? cluster_id
          : userData?.data?.accountData?.clusterid
            ? encode(`${userData?.data?.accountData?.clusterid}`, "ci_")
            : id
              ? encode(decode(id || "")?.split("_")[1], "ci_")
              : "";
        // Without a cluster there is no login page to build — "//login" is an
        // invalid URL that crashes history.replaceState.
        navigate(clusterId ? `/${clusterId}/login` : INVALID_PATH, {
          replace: true,
          state: clusterId ? undefined : invalidRouteState("not-found"),
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.pathname]);

  useEffect(() => {
    let intervalId: any;
    let timeoutId: any;

    const runTokenCheck = () => {
      if (!isExpired && !isExpiry) {
        dispatch(checkTokenExpire({}));
      }
    };

    if (isLogin) {
      // Notification polling runs every 30 seconds. Starting this check at
      // 2m45s keeps the recurring 3-minute token request 15 seconds apart.
      timeoutId = setTimeout(() => {
        runTokenCheck();
        intervalId = setInterval(runTokenCheck, TOKEN_CHECK_INTERVAL_MS);
      }, TOKEN_CHECK_INITIAL_DELAY_MS);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isLogin, dispatch]);

  const handleSetPreviewType = async (val: string) => {
    await dispatch(setPreviewType(val));
  };

  useEffect(() => {
    const getNotificationCount = async () => {
      try {
        let payload = {
          accounts_id: userData?.data?.accountData?.id,
        };
        await dispatch(notificationCountGet(payload));
      } catch (error) {
        console.log("error =====", error);
      }
    };

    let intervalId: any;

    if (isLogin) {
      intervalId = setInterval(() => {
        getNotificationCount();
      }, 30000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isLogin, dispatch]);

  useEffect(() => {
    const getPaymentMarkers = async () => {
      let clusterid = clusterId
        ? clusterId
        : userData?.data?.accountData?.clusterid;
      try {
        let params = {
          lead_id: accountLeadData?.lead_id,
          cluster_id: clusterid,
        };
        await dispatch(paymentMarkersGet(params));
      } catch (error) {
        console.log("error =====", error);
      }
    };
    if (isLogin && accountLeadData?.lead_id && !paymentMarkers?.length) {
      getPaymentMarkers();
    }
  }, [isLogin, accountLeadData]);

  const routes = [
    "agreement",
    "invoice",
    "quotes",
    "product",
    "payment_link",
    "subscriptions-plans",
    "payment-methods",
    "payments",
    "view-payment",
  ];

  useEffect(() => {
    if (
      location?.pathname?.split("/")[2] !== "make-payment" &&
      location?.pathname?.split("/")[2] !== "quick-checkout" &&
      location?.pathname?.split("/")[1] !== "view-payment" &&
      location?.pathname?.split("/")[1] !== "agreement" &&
      location?.pathname?.split("/")[1] !== "quotes" &&
      location?.pathname?.split("/")[1] !== "invoice" &&
      location?.pathname?.split("/")[1] !== "product" &&
      location?.pathname?.split("/")[1] !== "payment_link" &&
      location?.pathname?.split("/")[1] !== "subscribe" &&
      location?.pathname?.split("/")[1] !== "invalid"
    ) {
      setIsPaymentPage(false);
    } else {
      setIsPaymentPage(true);
    }
  }, [location]);

  const onPaymentDownload = async (id: string | number, receiptId?: any) => {
    setIsDownloading(id);
    let pid = encode(`${id}_${clusterId}_${new Date().getTime()}`, "pid_");
    try {
      let payload = {
        invoice_id: id,
        lead_id: accountLeadData?.lead_id,
        url: `${vanityUrl}view-payment/${pid}`,
        cluster_id: clusterId,
        isFromPortal: true,
      };

      await downloadPdfFromApi(
        () => dispatch(paymentReceiptDownloadPdf(payload)).unwrap(),
        `Receipt - ${receiptId || id}.pdf`,
        (loading) => setIsDownloading(loading ? id : "")
      );
    } catch (error: any) {
      console.log("payment download err: -->", error?.message);
    } finally {
      setIsDownloading("");
    }
  };

  const isPlaceholderPage = useMemo(() => {
    if (location?.pathname?.split("/")[2] === "payments") {
      if (lastTab === "payment") {
        return historyCount?.all === 0;
      } else {
        return scheduleHistoryCount?.AllMonthCount === 0;
      }
    } else if (location?.pathname?.split("/")[2] === "subscriptions-plans") {
      return subscriptionCount?.All === 0;
    } else if (location?.pathname?.split("/")[2] === "action-center") {
      return actionCount?.summary?.all === 0;
    } else if (location?.pathname?.split("/")[2] === "documents") {
      return attachmentList?.count === 0;
    }

    return false;
  }, [
    location,
    historyCount,
    lastTab,
    scheduleHistoryCount,
    actionCount,
    subscriptionCount,
    attachmentList,
  ]);

  return (
    <>
      <Helment />
      <TestIdScope scope="app-shell">
        {showOverlay && (
          <div
            className="absolute top-0 left-0 w-screen h-screen bg-bgPrimary z-10"
            data-testid={tid("guest-login-overlay")}
          >
            <Login
              guestLogin={true}
              setShowOverlay={async (val: boolean) =>
                await dispatch(updateOverlayState(val))
              }
            />
          </div>
        )}
        {/* Scales the whole shell — chrome, routed page and the panels that sit
            over it — down to the proportions of the 1920px design on narrower
            desktops. See --app-zoom in index.css. The drawers live inside it so
            a preview panel is sized consistently with the page behind it. */}
        <div className="portal-shell">
          <div
            className="h-[var(--shell-vh)] flex flex-col lg:block animate__animated animate__fadeIn"
            data-testid={tid("layout")}
          >
            {showWarn && !isPaymentPage ? (
              <div className="lg:max-h-12 lg:h-12 h-fit shrink-0 animate__animated animate__fadeInDown">
                <WarningBar
                  content={replaceMarkers(
                    portalSettings?.min_marinda_text,
                    paymentMarkers
                  )}
                  onCloseClick={() => dispatch(onCloseWarn(false))}
                />
              </div>
            ) : null}

            <div className="hidden lg:block">
              <Sidebar showWarn={showWarn} />
            </div>
            {!isDashPreview && (
              <div
                className={`block lg:hidden px-5 ${
                  isPaymentPage ? "fixed top-0" : "relative shrink-0"
                } w-full bg-bgPrimary z-10`}
              >
                <Navbar setOpenMenu={setOpenMenu} />
              </div>
            )}
            <div
              data-testid={tid("main-content")}
              className={` ${isSmallSidebar ? "lg:pl-[72px]" : "lg:pl-[254px]"} ${
                // pb-[55px] clears the fixed footer, which measures 52px. Without
                // the reserve the last slice of every page sits permanently
                // underneath the bar with no way to scroll it into view.
                !isPaymentPage && !isDashPreview
                  ? showWarn
                    ? // --shell-vh already excludes the notice here (see
                      // .under-warn-bar), so this is the room left below it.
                      "under-warn-bar flex-1 min-h-0 lg:h-[var(--shell-vh)] pb-[55px]"
                    : "flex-1 min-h-0 lg:h-[var(--shell-vh)] pb-[55px]"
                  : "h-[var(--shell-vh)] lg:scrollbar-auto scrollbar-hide"
              } overflow-y-auto overscroll-y-contain md:pt-0 pt-[3px] overflow-x-hidden transition-padding duration-300 ease-in-out`}
            >
              <Outlet />
            </div>
            {!isPaymentPage && !isDashPreview ? (
              <Footer
                className={`${isPlaceholderPage ? "lg:flex hidden" : ""} ${
                  isSmallSidebar
                    ? "lg:!w-[calc(var(--shell-vw)-72px)] lg:left-[72px]"
                    : "lg:!w-[calc(var(--shell-vw)-254px)] lg:left-[254px]"
                } lg:px-0 px-5 !text-txtParagraph transition-all duration-300 ease-in-out`}
              />
            ) : (
              <></>
            )}
          </div>
          {/* Mounted here, above the router, so the conversation and the panel
            showing it both survive navigation. Renders nothing unless the
            customer is signed in and the AI service accepts their token. */}
          <AiPanelHost />

          <Drawer
            enableOverlay={true}
            lockBackgroundScroll={true}
            direction="left"
            open={openMenu}
            data-testid={tid("mobile-navigation-drawer")}
            size={isLessThan306 ? `100vw` : "306px"}
            onClose={() => {
              setOpenMenu(false);
            }}
            overlayColor="#000"
          >
            <NavMenu setOpenMenu={setOpenMenu} />
          </Drawer>

          {/* Preview */}
          <Drawer
            enableOverlay={true}
            lockBackgroundScroll={true}
            direction="right"
            open={previewType === "payment"}
            data-testid={tid("payment-preview-drawer")}
            size={isLessThan590 ? `100vw` : "590px"}
            onClose={async () => {
              await dispatch(
                showPreview({
                  previewType: "",
                  previewId: null,
                })
              );
            }}
            overlayColor="#000"
          >
            <PaymentPreview
              id={previewId}
              setId={async (val: string | number) => {
                await dispatch(setPreviewId(val));
              }}
              show={previewType === "payment"}
              setShow={async (val: string | number) => {
                await dispatch(setPreviewType(val ? val : ""));
              }}
              isDownloading={isDownloading}
              onDownloadCLick={(id: string | number, receiptId?: any) =>
                onPaymentDownload(id, receiptId)
              }
            />
          </Drawer>
          <Drawer
            enableOverlay={true}
            lockBackgroundScroll={true}
            direction="right"
            open={previewType === "subscription"}
            data-testid={tid("subscription-preview-drawer")}
            size={isLessThan590 ? `100vw` : "590px"}
            onClose={async () => {
              await dispatch(
                showPreview({
                  previewType: "",
                  previewId: null,
                })
              );
            }}
            overlayColor="#000"
          >
            <SubscriptionsPreview
              id={previewId}
              setId={async (val: string | number) => {
                await dispatch(setPreviewId(val));
              }}
              show={previewType === "subscription"}
              setShow={async (val: string | number) => {
                await dispatch(setPreviewType(val ? val : ""));
              }}
              setHandleDrawerSize={() => {}}
              isScheduled={false}
            />
          </Drawer>
          <Drawer
            enableOverlay={true}
            lockBackgroundScroll={true}
            direction="right"
            open={previewType === "agreement" || previewType === "payment_link"}
            data-testid={tid("action-preview-drawer")}
            size={isLessThan590 ? `100vw` : "590px"}
            onClose={async () => {
              await dispatch(
                showPreview({
                  previewType: "",
                  previewId: null,
                })
              );
            }}
            overlayColor="#000"
          >
            <ActionPreview
              id={previewId}
              setId={async (val: string | number) => {
                await dispatch(setPreviewId(val));
              }}
              show={
                previewType === "agreement" || previewType === "payment_link"
              }
              setShow={async (val: string | number) => {
                await dispatch(setPreviewType(val ? val : ""));
              }}
              view={previewType}
              setView={async (val: string | number) => {
                await dispatch(setPreviewType(val ? val : ""));
              }}
            />
          </Drawer>

          <Drawer
            enableOverlay={true}
            lockBackgroundScroll={true}
            direction="right"
            open={previewType === "quotes"}
            data-testid={tid("quotes-preview-drawer")}
            size={isLessThan590 ? `100vw` : "590px"}
            onClose={async () => {
              await dispatch(
                showPreview({
                  previewType: "",
                  previewId: null,
                })
              );
            }}
            overlayColor="#000"
          >
            <QuotesPreview
              id={previewId}
              slug={previewId}
              setId={async (val: string | number) => {
                await dispatch(setPreviewId(val));
              }}
              show={previewType === "quotes"}
              setShow={async (val: string | number) => {
                await dispatch(setPreviewType(val ? val : ""));
              }}
              isSlug={false}
            />
          </Drawer>

          <Drawer
            enableOverlay={true}
            lockBackgroundScroll={true}
            direction="right"
            open={previewType === "invoice"}
            data-testid={tid("invoice-preview-drawer")}
            size={isLessThan590 ? `100vw` : "590px"}
            onClose={async () => {
              await dispatch(
                showPreview({
                  previewType: "",
                  previewId: null,
                })
              );
            }}
            overlayColor="#000"
          >
            <InvoicePreview
              id={previewId}
              slug={previewId}
              setId={async (val: string) => {
                await dispatch(setPreviewId(val));
              }}
              setIsPreview={(val: boolean) => {
                handleSetPreviewType(val && val === true ? "invoice" : "");
              }}
              isPreview={previewType === "invoice"}
              isSlug={false}
            />
          </Drawer>
        </div>
      </TestIdScope>
    </>
  );
};

export default Layout;
